import * as React from 'react';
import {Link as RouterLink} from 'react-router-dom';

import {SegmentedControl} from 'shells/card/segmented-control';
import {FlashBanner} from 'shells/flash-banner/flash-banner';
import {Stack} from 'shells/layout/stack/stack';
import {Link} from 'shells/link/link';
import type {SaveState} from 'shells/saved-setting-toast/use-manage-save-state';
import {Body} from 'shells/typography/body';
import {LabelWithContent} from 'shells/typography/label-with-content';

import type {
    ChatbotResponseTimingEnum,
    ChatbotToneEnum,
    ChatbotAnswerLengthEnum,
} from 'nutshell-graphql-types';
import type {
    GetBehaviorSettings_chatSettings as BehaviorSettings,
    ChatSettingsInput,
} from 'nutshell-graphql-types';

import {
    SAVE_STATE_KEYS,
    CHATBOT_RESPONSE_TIMING_OPTIONS,
    CHATBOT_TONE_OPTIONS,
    CHATBOT_ANSWER_LENGTH_OPTIONS,
} from '../consts';
import type {SaveStateKey} from '../consts';

type Props = {
    chatSettings: BehaviorSettings;
    responseTiming: ChatbotResponseTimingEnum;
    setResponseTiming: (value: ChatbotResponseTimingEnum) => void;
    hasNoContent: boolean;
    tone: ChatbotToneEnum;
    setTone: (value: ChatbotToneEnum) => void;
    savingToastKey: SaveStateKey | null | undefined;
    toastState: SaveState;
    handleSave: (updates: ChatSettingsInput, toastKey: SaveStateKey) => void;
    isDisabled: boolean;
};

export function BehaviorSection(props: Props) {
    const [answerLength, setAnswerLength] = React.useState<ChatbotAnswerLengthEnum>(
        props.chatSettings.chatbot.answerLength
    );

    return (
        <Stack spacing={16}>
            <Body size='large' isBold={true}>
                Behavior
            </Body>
            <LabelWithContent
                label='Chatbot will respond'
                state={
                    props.savingToastKey === SAVE_STATE_KEYS.RESPONSE_TIMING
                        ? props.toastState
                        : 'initial'
                }
                spacing={8}
                content={
                    <Stack spacing={8}>
                        <SegmentedControl
                            buttonOptions={CHATBOT_RESPONSE_TIMING_OPTIONS}
                            selectedValue={props.responseTiming}
                            setSelectedValue={(value) => {
                                props.setResponseTiming(value);
                                props.handleSave(
                                    {chatbot: {responseTiming: value}},
                                    SAVE_STATE_KEYS.RESPONSE_TIMING
                                );
                            }}
                            isAnimated={true}
                            isDisabled={props.hasNoContent}
                        />
                        {props.hasNoContent && (
                            <Body isItalic={true} size='tiny'>
                                <Link
                                    to='/inbox/settings/chat/knowledge'
                                    // @ts-expect-error
                                    as={RouterLink}
                                    variant='primary'
                                    size='tiny'
                                >
                                    Add content from your website
                                </Link>{' '}
                                to train your chatbot.
                            </Body>
                        )}
                    </Stack>
                }
            />
            {props.responseTiming === 'NEVER' && (
                <FlashBanner type='info' hasBorder={true}>
                    You’ve disabled the chatbot. Users can still interact under the handoff rules or
                    the other actions in the engagement bar.
                </FlashBanner>
            )}
            <div className={props.isDisabled ? 'disabled' : ''}>
                <LabelWithContent
                    label='Conversation tone'
                    state={
                        props.savingToastKey === SAVE_STATE_KEYS.TONE ? props.toastState : 'initial'
                    }
                    spacing={4}
                    content={
                        <SegmentedControl
                            buttonOptions={CHATBOT_TONE_OPTIONS}
                            selectedValue={props.tone}
                            setSelectedValue={(value) => {
                                if (!props.isDisabled) {
                                    props.setTone(value);
                                    props.handleSave(
                                        {chatbot: {tone: value}},
                                        SAVE_STATE_KEYS.TONE
                                    );
                                }
                            }}
                            isAnimated={true}
                            isDisabled={props.isDisabled}
                        />
                    }
                />
            </div>
            <div className={props.isDisabled ? 'disabled' : ''}>
                <LabelWithContent
                    label='Answer Length'
                    state={
                        props.savingToastKey === SAVE_STATE_KEYS.ANSWER_LENGTH
                            ? props.toastState
                            : 'initial'
                    }
                    spacing={4}
                    content={
                        <SegmentedControl
                            buttonOptions={CHATBOT_ANSWER_LENGTH_OPTIONS}
                            selectedValue={answerLength}
                            setSelectedValue={(value) => {
                                if (!props.isDisabled) {
                                    setAnswerLength(value);
                                    props.handleSave(
                                        {chatbot: {answerLength: value}},
                                        SAVE_STATE_KEYS.ANSWER_LENGTH
                                    );
                                }
                            }}
                            isAnimated={true}
                            isDisabled={props.isDisabled}
                        />
                    }
                />
            </div>
        </Stack>
    );
}
